import { lazy, useEffect, useState } from "react";
import { createBrowserRouter,  useNavigate } from "react-router-dom";
import { prodClient, routes } from "../utils/constants";
import { Box, CircularProgress } from "@mui/material";
import { authenticateApp } from "../hook/auth-context";
import { NavigationProvider } from "../hook/route-hook";
import { useAppState } from "../hook/app-state";
import { redirectRouteCheck } from "../utils/eira/scorehelper";
import authService from "../services/authService";

// Lazy Load Components
const Profile = lazy(() => import("../pages/Profile/Profile"));
const ReviewHealthScoreAnsersWrapper = lazy(() => import("../pages/tests/HealthScores/ReviewHealthScoreAnsersWrapper"));
const ReportTrends = lazy(() => import("../pages/Result/ReportTrends/ReportTrends"));
const LabReportOptionSelect = lazy(() => import("../pages/tests/BloodReportsNew/LabReportOptionSelect"));
const LabReportUpload = lazy(() => import("../pages/tests/BloodReportsNew/LabReportsUpload"));
const RecommendedTestsLabReportwithCart = lazy(() => import("../pages/tests/BloodReportsNew/RecommendedTestsLabReportwithCart"));
const TongueAnalysisNewOptionSelect = lazy(() => import("../pages/tests/TongueAnalysisNew/TongueAnalysisNew"));
const TongueAnalysisUpload = lazy(() => import("../pages/tests/TongueAnalysisNew/TongueAnalysisUpload"));
const TongueAnalysisTakePicture = lazy(() => import("../pages/tests/TongueAnalysisNew/TongueAnalysisTakePicture"));
const PanchkarmaLanding = lazy(() => import("../pages/Panchkarma/PanchkarmaLanding"));
const PanchkarmaCenterSelection = lazy(() => import("../pages/Panchkarma/PanchkarmaCenterSelection"));
const PanchkarmaCenterDetails = lazy(() => import("../pages/Panchkarma/PanchkarmaCenterDetails"));
const DateSelection = lazy(() => import("../pages/Panchkarma/DateSelection"));
const RoomSelection = lazy(() => import("../pages/Panchkarma/RoomSelection"));
const LocationPick = lazy(() => import("../pages/Panchkarma/LocationPick"));
const AddAddress = lazy(() => import("../pages/Panchkarma/AddAddress"));
const Payment = lazy(() => import("../pages/Panchkarma/Payement"));
const AddCard = lazy(() => import("../pages/Panchkarma/AddCard"));
const TreatmentOverview = lazy(() => import("../pages/Panchkarma-Review/TreatmentOverview"));
const FirstConsultationReport = lazy(() => import("../pages/Panchkarma-Review/ConsultationReport"));
const SymptomRatings = lazy(() => import("../pages/Panchkarma-Review/SymptomRatings"));
const PanchkarmaDailyScheduleReport = lazy(() => import("../pages/Panchkarma-Review/PanchkarmaDailySchedule"));
const PanchkarmaFood = lazy(() => import("../pages/Panchkarma-Review/PanchkarmaFood"));
const PanchkarmaMedicines = lazy(() => import("../pages/Panchkarma-Review/PanchkarmaMedicines"));
const PanchkarmaMantra = lazy(() => import("../pages/Panchkarma-Review/PanchkarmaMantra"));
const PanchkarmaKriya = lazy(() => import("../pages/Panchkarma-Review/PanchkarmaKriya"));
const AboutEira = lazy(() => import("../pages/AboutEira/AboutEira"));
const NotFound = lazy(() => import("../pages/404Page/NotFound"));
const PrakritiResponse = lazy(() => import("../pages/Result/PrakritiResponse"));
const AboutTest = lazy(() => import("../pages/about-test/AboutTest"));
const LanguageSelection = lazy(() => import("../pages/language-selection/LanguageSelection"));
const Consent = lazy(() => import("../pages/consent/Consent"));
const PrescriptionScreen = lazy(() => import("../pages/Prescription/Prescription"));
const SymtomsListScreen = lazy(() => import("../pages/DashboardNew/SymtomsListScreen"));
const TermsCondition = lazy(() => import("../pages/Policies/TermsCondition"));
const PrivacyPolicy = lazy(() => import("../pages/Policies/PrivacyPolicy"));
const PatientAppLayout = lazy(() => import("../layouts/PatientAppLayout"));



const Login = lazy(() => import("../pages/Otplogin/Login"));
const BasicDetails = lazy(() => import("../pages/Onboarding/BasicDetails"));
const Symptom = lazy(() => import("../pages/symptom/Symptom"));
const SymptomsForm = lazy(() => import("../pages/symptom/SymptomsForm"));
const RecommendedTests = lazy(() => import("../pages/tests/RecommendedTests"));
const HealthHistory = lazy(() => import("../pages/tests/HealthHistory"));
const Prakriti = lazy(() => import("../pages/tests/Prakriti"));
const Vikriti = lazy(() => import("../pages/tests/Vikriti"));
const LabReportResult = lazy(() =>
  import("../pages/tests/BloodReports/LabReportResult").then((m) => ({
    default: m.LabReportResult,
  }))
);
const AstralReport = lazy(() => import("../pages/tests/AstralReport"));
const HealthScores = lazy(
  () => import("../pages/tests/HealthScores/HealthScores")
);
const ScoreResult = lazy(() => import("../pages/Helper/ScoreReult"));
const Result = lazy(() => import("../pages/Result/Result"));
const CommonScoreQuestionScreen = lazy(
  () => import("../pages/tests/HealthScores/CommonScoreQuestionScreen")
);
const AppointmentDashboard = lazy(
  () => import("../pages/appointment/AppointmentDashboard")
);
const ViewAllDoctors = lazy(
  () => import("../pages/appointment/ViewAllDoctors")
);
const DoctorProfile = lazy(() => import("../pages/appointment/DoctorsProfile"));
const SelectTimeSlot = lazy(() => import("../pages/appointment/TimeSlot"));
const AppointmentSummary = lazy(
  () => import("../pages/appointment/AppointmentSummary")
);
const AppointmentDetails = lazy(
  () => import("../pages/appointment/AppointmentDeatils")
);
const AppointmentCancellation = lazy(
  () => import("../pages/appointment/AppointmentCancellation")
);
const AppointmentCancellationConfirmed = lazy(
  () => import("../pages/appointment/AppointmentCancellationConfirmed")
);
const UpcomingAppointments = lazy(
  () => import("../pages/appointment/UpcomingAppointments")
);
const NewUserDashboard = lazy(
  () => import("../pages/DashboardNew/NewUserDashboard")
);
const AllTests = lazy(() => import("../pages/AllTests/AllTests"));
const Medicines = lazy(() => import("../pages/Medicines/Medicines"));
const CheckList = lazy(() => import("../pages/dashboard/CheckList"));
const AyurvedaCorrelation = lazy(
  () => import("../pages/tests/AyurvedaCorrelation")
);

// Simplified PrivateRoute - just checks authentication, no navigation logic
function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { authStatus, initializingApp } = authenticateApp();
  const { currentClinic, setHomeRouteLocation } = useAppState();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const check = async () => {
      try {
        if (initializingApp) return;

        const isAuthenticated = await authService.isAuthenticated();
        const clinicUserId = await authService.getClinicUserId();

        if (!isAuthenticated || !clinicUserId || authStatus !== "success") {
          navigate(routes.AboutEira, { replace: true });
          return;
        }

        const redirectResult = await redirectRouteCheck(
          clinicUserId,
          currentClinic,
          ["userDetails", "userSymptoms", "healthHistory"]
        );

        setHomeRouteLocation(redirectResult.route);
        const currentPath = window.location.pathname;
        if (currentPath !== redirectResult.route) {
          navigate(redirectResult.route, { replace: true });
        }
      } catch (err) {
        console.error("PrivateRoute check error:", err);
        navigate(routes.AboutEira, { replace: true });
      } finally {
        setLoading(false);
      }
    };

    check();
  }, [authStatus, initializingApp, currentClinic, navigate]);

  if (initializingApp || loading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return <>{children}</>;
}

const clinicRoutes = [
  // Public routes (no authentication required)
  {
    path: routes.AboutEira,
    element: <AboutEira />,
  },
  {
    element: (
      <NavigationProvider>
        <PatientAppLayout />
      </NavigationProvider>
    ),
    children: [
      {
        path: routes.Login,
        element: <Login />,
      },
      {
        path: routes.LanguageSelection,
        element: <LanguageSelection />,
      },
      {
        path: routes.TermsCondition,
        element: <TermsCondition />,
      },
      {
        path: routes.PrivacyProlicy,
        element: <PrivacyPolicy />,
      },
    ],
  },

  // Protected routes with AuthGuard at the top level
  {
    element: (
      <PrivateRoute>
        <NavigationProvider>
          <PatientAppLayout />
        </NavigationProvider>
      </PrivateRoute>
    ),

    children: [
      {
        path: routes.Profile,
        element: <Profile />,
      },
      {
        path: routes.BasicDetails,
        element: <BasicDetails />,
      },
      {
        path: routes.Symptom,
        element: <Symptom />,
      },
      {
        path: routes.SymptomForm,
        element: <SymptomsForm />,
      },
      {
        path: routes.RecommendedTests,
        element: <RecommendedTests />,
      },
      // tests routes
      {
        path: routes.HealthHistory,
        element: <HealthHistory />,
      },
      {
        path: routes.AyurvedaCorrelation,
        element: <AyurvedaCorrelation />,
      },
      {
        path: routes.PrakritiTest,
        element: <Prakriti />,
      },
      {
        path: routes.PrakritiResponse,
        element: <PrakritiResponse />,
      },
      // Vikriti test
      {
        path: routes.VikritiAnalysis,
        element: <Vikriti />,
      },
      // new tongue routes
      {
        path: routes.TongueAnalysisNew,
        element: <TongueAnalysisNewOptionSelect />,
      },
      {
        path: routes.TongueAnalysisUpload,
        element: <TongueAnalysisUpload />,
      },
      {
        path: routes.TongueAnalysisTakePicture,
        element: <TongueAnalysisTakePicture />,
      },
      {
        path: routes.LabReports,
        element: <LabReportOptionSelect />,
      },
      {
        path: routes.LabReportsUpload,
        element: <LabReportUpload />,
      },
      {
        path: routes.RecommendedTestsLabReportWithCart,
        element: <RecommendedTestsLabReportwithCart />,
      },
      {
        path: `${routes.LabReports}/:userId`,
        element: <LabReportResult />,
      },
      {
        path: `${routes.ReportTrends}/:userId`,
        element: <ReportTrends />,
      },
      {
        path: `${routes.DiseaseReport}/:userId`,
        element: <Result />,
      },
      {
        path: routes.AstralHealth,
        element: <AstralReport />,
      },
      {
        path: routes.HealthScores,
        element: <HealthScores />,
      },
      {
        path: `${routes.HealthScores}/:scoreName`,
        element: <CommonScoreQuestionScreen />,
      },
      {
        path: `${routes.ScoreResult}/:scoreName`,
        element: <ScoreResult />,
      },
      {
        path: `${routes.ScoreResult}/:score`,
        element: <Result />,
      },
      // dashboard routes
      {
        path: `${routes.ViewAllDoctors}/:speciality`,
        element: <ViewAllDoctors />,
      },
      {
        path: `${routes.ViewAllDoctors}`,
        element: <ViewAllDoctors />,
      },
      {
        path: `${routes.DcotorsProfile}/:docId`,
        element: <DoctorProfile />,
      },
      {
        path: `${routes.BookTimeSlot}/:docId`,
        element: <SelectTimeSlot />,
      },
      {
        path: `${routes.AppointmentSummary}/:docId`,
        element: <AppointmentSummary />,
      },
      {
        path: `${routes.AppointmentDetails}/:appointmentId`,
        element: <AppointmentDetails />,
      },
      {
        path: `${routes.CancelAppointment}/:appointmentId`,
        element: <AppointmentCancellation />,
      },
      {
        path: `${routes.CancelAppointmentConfirmation}/:appointmentId`,
        element: <AppointmentCancellationConfirmed />,
      },
      {
        path: `${routes.UpcomingAppointments}`,
        element: <UpcomingAppointments />,
      },
      {
        path: `${routes.Dashboard}`,
        element: <NewUserDashboard />,
      },
      {
        path: `${routes.CheckList}`,
        element: <CheckList />,
      },
      {
        path: `${routes.ReviewAnswers}`,
        element: <ReviewHealthScoreAnsersWrapper />,
      },
      {
        path: routes.Appointment,
        element: <AppointmentDashboard />,
      },
      // medicines
      {
        path: `${routes.Medicines}`,
        element: <Medicines />,
      },
      // All tests
      {
        path: `${routes.Tests}`,
        element: <AllTests />,
      },
      // Panchkarma routes
      {
        path: `${routes.PanchKarmaLandingScreen}`,
        element: <PanchkarmaLanding />,
      },
      {
        path: `${routes.PanchkarmaCenter}`,
        element: <PanchkarmaCenterSelection />,
      },
      {
        path: `${routes.PanchkarmaCenter}/:clinicId`,
        element: <PanchkarmaCenterDetails />,
      },
      {
        path: `${routes.PanchkarmaDateSelection}`,
        element: <DateSelection />,
      },
      {
        path: `${routes.PanchkarmaRoomSelection}`,
        element: <RoomSelection />,
      },
      {
        path: `${routes.AddAddress}`,
        element: <AddAddress />,
      },
      {
        path: `${routes.AddLoction}`,
        element: <LocationPick />,
      },
      {
        path: `${routes.Payment}`,
        element: <Payment />,
      },
      {
        path: `${routes.AddCard}`,
        element: <AddCard />,
      },
      {
        path: `${routes.TreatmentOverView}`,
        element: <TreatmentOverview />,
      },
      {
        path: `${routes.FirstConsultationReport}`,
        element: <FirstConsultationReport />,
      },
      {
        path: `${routes.SymptomRating}`,
        element: <SymptomRatings />,
      },
      {
        path: `${routes.PanchkarmaDailySchedule}`,
        element: <PanchkarmaDailyScheduleReport />,
      },
      {
        path: `${routes.PanchkarmaDailyScheduleFood}`,
        element: <PanchkarmaFood />,
      },
      {
        path: `${routes.PanchkarmaDailyScheduleMedicine}`,
        element: <PanchkarmaMedicines />,
      },
      {
        path: `${routes.PanchkarmaDailyScheduleMantra}`,
        element: <PanchkarmaMantra />,
      },
      {
        path: `${routes.PanchkarmaDailyScheduleKriya}`,
        element: <PanchkarmaKriya />,
      },
      {
        path: `${routes.AboutTest}/:test`,
        element: <AboutTest />,
      },
      {
        path: `${routes.Consent}`,
        element: <Consent />,
      },
      {
        path: `${routes.Prescription}`,
        element: <PrescriptionScreen />,
      },
      {
        path: `${routes.SymtpomsList}`,
        element: <SymtomsListScreen />,
      },
    ],
  },

  // Catch-all route
  {
    path: "*",
    element: <NotFound />,
  },
];

// Modify initRouting to take currentUser as an argument
export const initRouting = (currentUser: string) => {
  if (currentUser === prodClient) return createBrowserRouter(clinicRoutes);
};
